import PropTypes from 'prop-types';
import { Tooltip } from '@arkestro/arkestro-design-system';
import { CloseOutlined, InfoCircleFilled } from '@ant-design/icons';
import useTranslation from 'next-translate/useTranslation';
import styles from './InputField.module.scss';

const ERROR_STYLE = { color: '#cd4141', fontSize: 12, marginBottom: 5 };
const EMPTY_STYLE = {};
/** @type {(...args: any[]) => void} */
const noop = () => {};

const InputField = ({
  id,
  labelText,
  name,
  onChange = noop,
  onKeyPress = noop,
  value = '',
  required = false,
  textArea = false,
  rows = 5,
  tooltipText,
  onRemove,
  hasBorders = false,
  error,
  autoFocus = false,
  placeholder = '',
  style = EMPTY_STYLE,
  readOnly = false,
  disabled = false,
  type = 'text',
  onClick = noop,
  maxLength,
  onBlur = noop,
  testRef,
  showOptional = false,
}) => {
  const attrs = {
    autoFocus,
    'data-testid': testRef,
    disabled,
    id: id || name,
    name,
    onChange,
    onClick,
    onKeyPress,
    placeholder,
    readOnly,
    required,
    value
  };

  if (maxLength) attrs.maxLength = maxLength;

  const { t } = useTranslation('common');
  const errorClass = error && error.length !== 0 ? styles.withError : '';
  const inputFieldClass = hasBorders ? styles.withBorders : '';
  const textInput = (
    <input
      className={`${styles.input} ${inputFieldClass} ${errorClass}`}
      disabled={disabled}
      name={name}
      onBlur={onBlur}
      style={style}
      type={type}
      {...attrs}
    />
  );
  const textAreaInput = (
    <textarea
      className={`${styles.textarea} ${styles.withBorders} ${errorClass}`}
      disabled={disabled}
      name={name}
      onBlur={onBlur}
      rows={rows}
      style={style}
      {...attrs}
    />
  );

  return (
    <>
      <div style={{ position: 'relative' }}>
        {labelText && (
          <label className={styles.label} htmlFor={name}>
            {labelText}
            {required && <span>&nbsp;*</span>}
            {!required && showOptional && <span> ({t('general.optional')})</span>}
            {tooltipText && tooltipText.length > 0 && (
              <Tooltip overlayStyle={{ minWidth: 300 }} placement="bottom" title={tooltipText}>
                <InfoCircleFilled style={{ fontSize: 12, marginLeft: 10 }} />
              </Tooltip>
            )}
          </label>
        )}
        {textArea ? textAreaInput : textInput}
        {onRemove && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <span
            className={styles.closeButton}
            data-testid='remove-btn'
            onClick={onRemove}
            role='button'
            tabIndex={0}
          >
            <CloseOutlined />
          </span>
        )}
      </div>
      {error && <div style={ERROR_STYLE}>{error}</div>}
    </>
  );
};

InputField.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  hasBorders: PropTypes.bool,
  id: PropTypes.string,
  labelText: PropTypes.string,
  maxLength: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onKeyPress: PropTypes.func,
  onRemove: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  rows: PropTypes.number,
  showOptional: PropTypes.bool,
  style: PropTypes.shape(),
  testRef: PropTypes.string,
  textArea: PropTypes.bool,
  tooltipText: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
};

export default InputField;
